import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Icons } from '../../../shared/helpers/icons';
import { BuildingLevel } from '../../../shared/models/building';
import { BuildingService } from '../../../shared/services/building.service';
import { BuildingContextService } from '../../../shared/services/building-context.service';
import {
  AggregatedBulkResult,
  BulkBatchController,
  BulkBatchProgress,
  StreetviewService
} from '../../../shared/services/streetview.service';
import { environment } from '../../../environments/environment';
import { endpoints } from '../../../shared/endpoints';
import { BatchProgressView, ResultRow, SkippedRow, UploadPhase } from '../../../shared/models/bulkUpload';

// Phase the user is currently in. Drives the overlay copy so they always
// know whether the browser is still reading the zip, sending bytes, or
// waiting on the server's image pipeline.

@Component({
  selector: 'app-bulk-upload-360',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './bulk-upload-360.component.html'
})
export class BulkUpload360Component implements OnInit, OnDestroy {
  icons = Icons;

  // Building comes from the navbar via BuildingContextService — read-only here.
  readonly selectedBuilding = computed(() => this.buildingContext.selected());

  // Levels are loaded reactively whenever the selected building changes.
  levels = signal<BuildingLevel[]>([]);
  loadingLevels = signal(false);

  // Form state owned by this page.
  selectedLevelId = signal<number | null>(null);
  zipFile = signal<File | null>(null);
  geojsonFile = signal<File | null>(null);
  logoFile = signal<File | null>(null);
  replaceExisting = signal(false);

  // Upload state.
  busy = signal(false);
  progress = signal<BatchProgressView | null>(null);
  // Persistent error banner — survives until the next submit. 
  errorBanner = signal<{ kind: 'conflict' | 'generic'; message: string } | null>(null);
  private batchController: BulkBatchController | null = null;

  // Results.
  showResults = signal(false);
  matchedCount = signal(0);
  skippedRows = signal<SkippedRow[]>([]);
  unmatchedJpgs = signal<{ fileName: string; pointId: number | string | null }[]>([]);
  unmatchedPointIds = signal<(number | string)[]>([]);
  unindexableCount = signal(0);
  resultRows = signal<ResultRow[]>([]);

  targetUrl = computed(() => {
    const b = this.selectedBuilding();
    return (
      environment.apiBaseUrl +
      endpoints.bulkUploadStreetviews.replace(':buildingId', b ? String(b.id) : '…')
    );
  });

  skippedByReason = computed(() => {
    const groups: Record<string, SkippedRow[]> = {};
    for (const r of this.skippedRows()) {
      const key = r.reason || 'unknown';
      (groups[key] ||= []).push(r);
    }
    const labels: Record<string, string> = {
      no_geojson_match: 'No GeoJSON match',
      duplicate_content: 'Already uploaded (byte-identical photo for this building)',
      unknown: 'Other'
    };
    return ['no_geojson_match', 'duplicate_content', 'unknown']
      .filter(k => groups[k]?.length)
      .map(k => ({ key: k, label: labels[k] || k, rows: groups[k] }));
  });

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private buildingService: BuildingService,
    private buildingContext: BuildingContextService,
    private streetview: StreetviewService,
    private toastr: ToastrService
  ) {
    // Reactive level pipeline — fires once on subscribe (with the current
    // building) and again every time the navbar picks a different one.
    // `switchMap` cancels the in-flight fetch if the user switches buildings
    // before the previous response lands, so the dropdown can't end up with
    // the wrong building's levels. Auto-unsubscribes on component destroy.
    toObservable(this.buildingContext.selected)
      .pipe(
        tap(building => {
          this.selectedLevelId.set(null);
          this.levels.set([]);
          this.loadingLevels.set(!!building);
        }),
        switchMap(building =>
          building
            ? this.buildingService.getLevels(building.id).pipe(
                catchError(() => {
                  this.toastr.error('Failed to load levels.');
                  return of<BuildingLevel[]>([]);
                })
              )
            : EMPTY
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(levels => {
        this.levels.set(levels);
        this.loadingLevels.set(false);
      });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.batchController?.abort();
  }

  // ---- File pickers ----
  onZipChange(ev: Event): void {
    this.zipFile.set((ev.target as HTMLInputElement).files?.[0] ?? null);
  }
  onGeoJsonChange(ev: Event): void {
    this.geojsonFile.set((ev.target as HTMLInputElement).files?.[0] ?? null);
  }
  onLogoChange(ev: Event): void {
    this.logoFile.set((ev.target as HTMLInputElement).files?.[0] ?? null);
  }

  // ---- Submit ----
  submit(): void {
    const building = this.selectedBuilding();
    const levelId = this.selectedLevelId();
    const zip = this.zipFile();
    const geo = this.geojsonFile();

    if (!building) {
      this.toastr.error('Select a building from the top navbar first.');
      return;
    }
    if (!levelId) {
      this.toastr.error('Please select a level.');
      return;
    }
    if (!zip) {
      this.toastr.error('ZIP file is required.');
      return;
    }
    if (!geo) {
      this.toastr.error('GeoJSON file is required.');
      return;
    }

    if (this.replaceExisting()) {
      const ok = window.confirm(
        "You're about to PERMANENTLY delete every existing streetview, " +
          'feature, and cloud-storage image currently linked to this level ' +
          'before uploading the new batch.\n\nContinue?'
      );
      if (!ok) return;
    }

    this.resetResults();
    this.errorBanner.set(null);
    this.busy.set(true);
    this.progress.set({
      totalBatches: 0,
      currentBatch: 0,
      batchPercent: 0,
      overallPercent: 0,
      phase: 'reading',
      totalJpgs: 0
    });

    this.batchController = this.streetview.uploadBulkBatched(
      {
        buildingId: building.id,
        levelId,
        zipFile: zip,
        geojsonFile: geo,
        logoFile: this.logoFile(),
        replaceExisting: this.replaceExisting(),
        jpgsPerBatch: 5
      },
      ev => this.handleBatchEvent(ev)
    );
  }

  cancel(): void {
    if (!this.batchController) return;
    this.batchController.abort();
    this.batchController = null;
    this.busy.set(false);
    this.progress.set(null);
    this.toastr.warning(
      'Upload cancelled. Already-committed batches stay on the server — re-run to resume (dedup will skip duplicates) or use Replace existing to start clean.'
    );
  }

  private handleBatchEvent(ev: BulkBatchProgress): void {
    switch (ev.type) {
      case 'split':
        this.progress.update(p =>
          p
            ? {
                ...p,
                phase: 'uploading',
                totalBatches: ev.totalBatches,
                totalJpgs: ev.totalJpgs,
                currentBatch: 0,
                batchPercent: 0,
                overallPercent: 0
              }
            : p
        );
        break;

      case 'batch_start':
        this.progress.update(p =>
          p
            ? {
                ...p,
                phase: 'uploading',
                currentBatch: ev.batchIndex,
                totalBatches: ev.totalBatches,
                batchPercent: 0
              }
            : p
        );
        break;

      case 'progress': {
        // Once the XHR upload is at 100%, the server is still working — flip
        // the phase label so the user knows we're now waiting on processing.
        const phase: UploadPhase = ev.batchPercent >= 80 ? 'processing' : 'uploading';
        this.progress.update(p =>
          p
            ? {
                ...p,
                phase,
                currentBatch: ev.batchIndex,
                totalBatches: ev.totalBatches,
                batchPercent: ev.batchPercent,
                overallPercent: ev.overallPercent
              }
            : p
        );
        break;
      }

      case 'batch_done':
        // The next batch_start (or all_done) will reset the phase. 
        break;

      case 'all_done':
        this.busy.set(false);
        this.progress.set(null);
        this.batchController = null;
        this.renderAggregated(ev.aggregated);
        break;

      case 'error': {
        this.busy.set(false);
        this.progress.set(null);
        this.batchController = null;
        const msg = ev.message || 'Upload failed.';
        const isConflict = ev.httpStatus === 409;
        // Conflicts don't auto-dismiss 
        this.toastr.error(msg, isConflict ? 'Upload blocked' : 'Upload failed', {
          disableTimeOut: isConflict,
          closeButton: true,
          tapToDismiss: !isConflict
        });
        this.errorBanner.set({
          kind: isConflict ? 'conflict' : 'generic',
          message: msg
        });
        break;
      }
    }
  }

  private renderAggregated(agg: AggregatedBulkResult): void {
    this.matchedCount.set(agg.matchedCount || 0);
    this.skippedRows.set(agg.skippedJpgs || []);
    this.unmatchedJpgs.set(agg.unmatchedJpgs || []);
    this.unmatchedPointIds.set(agg.unmatchedPointIds || []);
    this.unindexableCount.set(agg.unindexableGeojsonFeatureCount || 0);
    this.resultRows.set(agg.results || []);
    this.showResults.set(true);

    const matched = agg.matchedCount || 0;
    const processed = agg.processedCount || 0;
    const skipped = agg.skippedJpgCount || 0;
    const unJpg = agg.unmatchedJpgCount || 0;
    const unPts = (agg.unmatchedPointIds || []).length;
    const unindex = agg.unindexableGeojsonFeatureCount || 0;
    const batches = agg.batchCount || 0;

    let summary = `Upload complete (${batches} batch${batches === 1 ? '' : 'es'}). ${matched} of ${processed} photo(s) linked to a GeoJSON point`;
    if (skipped) summary += `; ${skipped} photo(s) skipped`;
    if (unJpg) summary += `; ${unJpg} orphan streetview(s) detected`;
    if (unPts) summary += `; ${unPts} point(s) had no photo`;
    if (unindex) summary += `; ${unindex} GeoJSON feature(s) were unusable`;
    const hasIssues = unJpg || skipped || unPts || unindex;
    hasIssues ? this.toastr.info(summary) : this.toastr.success(summary);
  }

  private resetResults(): void {
    this.showResults.set(false);
    this.matchedCount.set(0);
    this.skippedRows.set([]);
    this.unmatchedJpgs.set([]);
    this.unmatchedPointIds.set([]);
    this.unindexableCount.set(0);
    this.resultRows.set([]);
  }
}
